export interface CropData {
  x: number;      // percentage from left (0-100)
  y: number;      // percentage from top (0-100)
  width: number;  // percentage of image width (0-100)
  height: number; // percentage of image height (0-100)
}

/**
 * Convert crop data to CSS styles for displaying a cropped region
 * of an image inside an overflow-hidden container.
 * 
 * Returns inline styles to apply to the <img> element.
 * The parent container must have overflow: hidden.
 */
export function getCropStyles(cropData: CropData | null | undefined): React.CSSProperties {
  if (!cropData || cropData.width <= 0 || cropData.height <= 0) {
    // No crop — return empty so classes handle layout
    return {};
  }

  const { x, y, width, height } = cropData;

  // Scale the image so the crop region fills the container
  const scaleX = 100 / width;
  const scaleY = 100 / height;

  // Position the image so the crop region is visible
  const translateX = -(x * scaleX);
  const translateY = -(y * scaleY);

  return {
    position: 'absolute' as const,
    width: `${scaleX * 100}%`,
    height: `${scaleY * 100}%`,
    left: `${translateX}%`,
    top: `${translateY}%`,
    objectFit: 'fill' as const,
    maxWidth: 'none',
  };
}

/**
 * Check if crop data is valid
 */
export function isValidCrop(crop: unknown): crop is CropData {
  if (!crop || typeof crop !== 'object') return false;
  const c = crop as Record<string, unknown>;
  return (
    typeof c.x === 'number' &&
    typeof c.y === 'number' &&
    typeof c.width === 'number' &&
    typeof c.height === 'number' &&
    c.width > 0 &&
    c.height > 0
  );
}
